import { snapshotValueEqual, type Shape } from '@vd/annotation-core';
import type { AuthSessionEpoch } from '../../lib/authSessionEpoch';

/**
 * The annotation saves this person has made from this browser tab, per
 * ticket, so a canvas can tell its own save coming back from somebody else's
 * change, and knows what that save was made from.
 *
 * Module-level because a save outlives the canvas that made it: the flush as
 * a ticket closes always does, and it is the canvas that opens next, on the
 * same ticket a moment later and from an older copy, that hears it come back.
 *
 * The answer is the save's basis: the working copy it was made from, before
 * any conflict retry folded a teammate's change into what was stored. The
 * canvas's shapes descend from that basis, so merging the stored shapes
 * against it keeps exactly what was done here since, an undo included, and
 * keeps the teammate's change the retry took in. Merged against the older
 * copy the canvas loaded instead, a shape added and then undone reads as the
 * server's addition and comes back; taken as it is, the teammate's change is
 * dropped.
 *
 * While a save is out it is known by each attempt: what it sent, at the one
 * version storing it could produce, the one after the version it expected.
 * Once answered, by the version the server stored it as, which is how a live
 * event, a refetched ticket and a conflict reply all name it. Only an error
 * the server answered with proves nothing was stored; a timeout or a lost
 * connection is an unknown outcome, so those attempts are kept until the
 * server's version shows they can no longer be stored (`observeServerVersion`).
 * RETAINED bounds the answered ones, which is ample since a copy older than a
 * canvas's newest version is refused before this is asked; OPEN_BOUND only
 * bounds memory, since open ones leave by their answer, an error or that
 * evidence.
 */
const RETAINED = 20;
const OPEN_BOUND = 100;

interface Attempt {
  sent: Shape[];
  expectedVersion: number;
}

interface Entry {
  operation: object;
  basis: Shape[];
  attempts: Attempt[];
  /** The stored version, once answered. */
  version?: number;
}

const saves = new Map<string, Entry[]>();

export function ownSavesKey(owner: AuthSessionEpoch, ticketId: string): string {
  return `${owner.epoch}:${owner.userId}:${ticketId}`;
}

function keep(key: string, entries: Entry[]): void {
  const answered = entries.filter((entry) => entry.version !== undefined).slice(-RETAINED);
  const open = entries.filter((entry) => entry.version === undefined).slice(-OPEN_BOUND);
  if (answered.length + open.length === 0) saves.delete(key);
  else saves.set(key, [...open, ...answered]);
}

/**
 * An attempt of `operation`, made from `basis`, has put `sent` to the server
 * expecting it to be at `expectedVersion`.
 */
export function recordSent(
  key: string,
  operation: object,
  basis: Shape[],
  sent: Shape[],
  expectedVersion: number,
): void {
  const entries = saves.get(key) ?? [];
  const entry = entries.find((candidate) => candidate.operation === operation);
  if (entry) {
    entry.attempts.push({ sent, expectedVersion });
    keep(key, entries);
  } else {
    keep(key, [...entries, { operation, basis, attempts: [{ sent, expectedVersion }] }]);
  }
}

/** `operation` was answered: the server stored it as `version`. */
export function recordAnswered(key: string, operation: object, version: number): void {
  const entries = saves.get(key) ?? [];
  const entry = entries.find((candidate) => candidate.operation === operation);
  if (!entry) return;
  keep(key, [
    ...entries.filter((candidate) => candidate !== entry),
    { ...entry, attempts: [], version },
  ]);
}

/**
 * Whether a save failed because the server answered it with an error, which
 * proves nothing was stored, rather than with no answer (a timeout, an abort,
 * a lost connection), which proves nothing. An answer the client could not
 * use, such as one without a version, was still an answer to a stored save.
 */
export function answeredWithError(error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === 'number' && status >= 400;
}

/** The server answered `operation` with an error: nothing it sent was stored. */
export function forgetSent(key: string, operation: object): void {
  keep(key, (saves.get(key) ?? []).filter((entry) => entry.operation !== operation));
}

/**
 * The server is at `version`, holding `snapshot`. An attempt still out that
 * expected an older version than the one before it can no longer be stored,
 * since the server refuses a save against a version it has moved past, and
 * one that expected the one before it but sent something else was beaten to
 * it. Neither can be what any later copy shows, so both go: a copy with no
 * version, from an older api build, could otherwise still match one by
 * content and be merged against its stale basis.
 */
export function observeServerVersion(key: string, version: number, snapshot: Shape[]): void {
  const entries = saves.get(key);
  if (!entries) return;
  const stillPossible = (attempt: Attempt) =>
    attempt.expectedVersion + 1 > version
    || (attempt.expectedVersion + 1 === version && snapshotValueEqual(attempt.sent, snapshot));
  keep(key, entries
    .map((entry) => (entry.version !== undefined ? entry : { ...entry, attempts: entry.attempts.filter(stillPossible) }))
    .filter((entry) => entry.version !== undefined || entry.attempts.length > 0));
}

/**
 * The basis of the save that a copy of the server's shapes, at `version`, is
 * one of, or null when it is somebody else's.
 */
export function ownSaveBasis(key: string, snapshot: Shape[], version: number | undefined): Shape[] | null {
  for (const entry of saves.get(key) ?? []) {
    if (entry.version !== undefined) {
      if (version !== undefined && entry.version === version) return entry.basis;
      continue;
    }
    const stored = entry.attempts.some((attempt) =>
      (version === undefined || version === attempt.expectedVersion + 1)
      && snapshotValueEqual(attempt.sent, snapshot));
    if (stored) return entry.basis;
  }
  return null;
}
